import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import { PrimaryButton, Stack, TextField, TooltipHost } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { FutureDatePicker } from 'common/components/FutureDatePicker';
import { UrgencyToggle } from 'common/components/UrgencySelector/UrgencyToggle';
import { ApprovalRequestInput } from 'common/types/globalTypes';
import { dateFormat } from 'common/utils/dateFormats';
import React, { useCallback, useEffect, useState } from 'react';

import { InvoiceDetails } from '../../graphql/__generated__/InvoiceDetails';
import {
  InvoiceSigningApprovalCreate,
  InvoiceSigningApprovalCreateVariables,
} from '../../graphql/__generated__/InvoiceSigningApprovalCreate';
import { useStyles } from '../index.styles';

import INVOICE_SIGNING_APPROVAL_CREATE from '../../graphql/InvoiceSigningApprovalCreate.graphql';
import INVOICE_DETAILS from '../../graphql/InvoiceDetails.graphql';

const CONFIRM_REQUEST_DIALOG_TITLE =
  'Are you sure you want to request this transaction for approval?';
const CONFIRM_REQUEST_DIALOG_SUBTEXT =
  'Your transaction will be requested for approval.';
const FINALIZED_BY_DATE_TOOLTIP =
  'Automatically promotes to urgent notifications when not approved by this date';

interface RequestApprovalProps {
  invoiceDetailsData?: InvoiceDetails;
  isNew: boolean;
  isDirty: boolean;
  onRequestStatus: (value: boolean) => void;
}

export const RequestApprovalTransaction: React.FC<RequestApprovalProps> = ({
  invoiceDetailsData,
  isNew,
  isDirty,
  onRequestStatus,
}) => {
  const [visible, setVisible] = useState(false);
  const styles = useStyles();
  const [requiredDate, setRequiredDate] = React.useState<string | undefined>();
  const [error, setError] = useState(false);
  const [requestComment, setRequestComment] = React.useState<string>();
  const [urgencyLevel, setUrgencyLevel] = React.useState<number>(1);
  const { _isStagedApprovalRequest } = { ...invoiceDetailsData?.invoice };

  const [invoiceSigningApprovalCreate, { loading }] = useMutation<
    InvoiceSigningApprovalCreate,
    InvoiceSigningApprovalCreateVariables
  >(INVOICE_SIGNING_APPROVAL_CREATE, {
    errorPolicy: 'all',
    onCompleted: () => {
      setUrgencyLevel(1);
    },
  });

  const onRequestStatusCb = useCallback(onRequestStatus, []);

  useEffect(() => {
    onRequestStatusCb(loading);
  }, [loading, onRequestStatusCb]);

  if (!isNew && _isStagedApprovalRequest)
    return (
      <Stack>
        <PrimaryButton
          disabled={isDirty}
          className={styles.diabledButton}
          text="Request Approval"
          onClick={() => setVisible(true)}
        />
        {visible && (
          <ConfirmDialog
            hidden={!visible}
            successBtnDisable={error}
            title={CONFIRM_REQUEST_DIALOG_TITLE}
            subText={CONFIRM_REQUEST_DIALOG_SUBTEXT}
            onDismiss={() => setVisible(false)}
            minWidth={500}
            onConfirm={async () => {
              setVisible(false);
              const inputVariables: ApprovalRequestInput = {
                entityId: invoiceDetailsData?.invoice?.id!,
                rowTimestamp: invoiceDetailsData?.invoice?._rowTimestamp!,
                comments: requestComment,
                requiredDate: requiredDate ? requiredDate : undefined,
              };
              const { errors } = await invoiceSigningApprovalCreate({
                variables: {
                  input: {
                    entityApproval: [inputVariables],
                    urgencyLevel: urgencyLevel,
                  },
                },
                awaitRefetchQueries: true,
                refetchQueries: [
                  {
                    query: INVOICE_DETAILS,
                    variables: {
                      id: invoiceDetailsData?.invoice?.id!,
                    },
                  },
                ],
              });

              if (errors?.length)
                showCustomToast({
                  content: errors[0].message,
                  appearance: 'error',
                });
              else {
                setRequestComment('');
                setRequiredDate('');
                showCustomToast({
                  content: 'Request sent for approval',
                  appearance: 'success',
                });
              }
            }}
          >
            <Stack tokens={{ childrenGap: 10 }}>
              <TextField
                className={styles.marginT10}
                multiline
                rows={3}
                value={requestComment}
                placeholder="Please write your comment here (optional)"
                resizable={false}
                onChange={(_event, value) => setRequestComment(value || '')}
              />
              <TooltipHost content={FINALIZED_BY_DATE_TOOLTIP}>
                <FutureDatePicker
                  onSelectDate={(date) => {
                    if (date) {
                      setRequiredDate(dateFormat(date!.toString()));
                    } else setRequiredDate(undefined);
                  }}
                  onValidationError={(error) => {
                    error ? setError(true) : setError(false);
                  }}
                  customPlaceholder='Needed by date (optional)'
                />
              </TooltipHost>
              <Stack tokens={{ padding: '6px 0px 0px 0px' }}>
                <UrgencyToggle
                  onToggle={(data) => {
                    setUrgencyLevel(data ? 0 : 1);
                  }}
                />
              </Stack>
            </Stack>
          </ConfirmDialog>
        )}
      </Stack>
    );
  else return null;
};
